import type { DateTime } from "luxon";
import { buildCanonicalFanOutMap } from "../../../utils/timeGrouping";
import type { ScheduleRow } from "../types";

/**
 * Each sheet-bucket entry holds one or more `[groupingKey, rows]` pairs (the sections that
 * landed on this sheet). Identical to today's `Map<string, Array<[string, ScheduleRow[]]>>`.
 */
export type SheetBucketMap = Map<string, Array<[string, ScheduleRow[]]>>;

/**
 * Section-key prefix detector for the post-merge dedup pass (mapping §7.2):
 * matches `HH:MM-HH:MM` at the start of a composite section key.
 */
const TIME_RANGE_PREFIX_REGEX = /^\d{2}:\d{2}-\d{2}:\d{2}/;

const buildSheetWindowMap = (sheetKeysMap: SheetBucketMap): Map<string, string[]> => {
	const sheetInfo: { key: string; start: DateTime; end: DateTime; count: number }[] = [];
	for (const [sheetKey, entries] of sheetKeysMap) {
		const firstRow = entries[0]?.[1]?.[0];
		if (!firstRow) continue;
		const totalRowCount = entries.reduce((sum, [, rows]) => sum + rows.length, 0);
		sheetInfo.push({ key: sheetKey, start: firstRow.shiftStart, end: firstRow.shiftEnd, count: totalRowCount });
	}
	sheetInfo.sort((a, b) => b.count - a.count);

	const asMinutes = sheetInfo.map(({ key, start, end }) => ({
		key,
		startMin: Math.floor(start.toMillis() / 60_000),
		endMin: Math.floor(end.toMillis() / 60_000),
	}));
	return buildCanonicalFanOutMap(asMinutes);
};

/**
 * Merge sheets whose shift-time windows are within the §6.3 proximity threshold (mapping §7.2).
 *
 * Sheets are ranked by row count; the most-populated window becomes the canonical anchor. Other
 * sheets fold into the closest anchor within the threshold. An unpopular wide sheet that fully
 * covers 2+ popular anchors instead fans its sections out across each of those anchors (so e.g. a
 * rare `7a-11p` sheet's rows appear on both the `7a-3p` and `3p-11p` tabs). Returns the new
 * sheet-bucket map keyed by canonical sheet labels.
 */
export const mergeOverlappingShiftSheets = (sheetKeysMap: SheetBucketMap): SheetBucketMap => {
	const sheetToTargets = buildSheetWindowMap(sheetKeysMap);
	const merged: SheetBucketMap = new Map();
	for (const [originalKey, entries] of sheetKeysMap) {
		const targets = sheetToTargets.get(originalKey) ?? [originalKey];
		for (const targetKey of targets) {
			// Copy section tuples when fanning into >1 target so the later dedup pass can't alias the
			// same section arrays across two sheets. (Row objects stay shared — read-only downstream.)
			const toAdd: Array<[string, ScheduleRow[]]> = targets.length > 1 ? entries.map(([k, rows]) => [k, [...rows]]) : entries;
			const existing = merged.get(targetKey);
			if (existing) existing.push(...toAdd);
			else merged.set(targetKey, [...toAdd]);
		}
	}
	return merged;
};

/**
 * Section-key dedup pass (mapping §7.2 step 3).
 *
 * After §7.2 merging, the same logical section may appear multiple times on one sheet — e.g. two
 * `"6a-2p | 2 West"` and `"6a-6p | 2 West"` entries both landing on the canonical `6a-6p` sheet.
 * This pass:
 * 1. Normalizes any time-range prefix in the section key to the canonical sheet's prefix
 * 2. Folds entries that now share a normalized key into a single entry (rows concatenated)
 */
export const dedupSectionsAfterShiftMerge = (sheetKeysMap: SheetBucketMap): SheetBucketMap => {
	const out: SheetBucketMap = new Map();
	for (const [sheetKey, groupEntries] of sheetKeysMap) {
		const deduped = new Map<string, [string, ScheduleRow[]]>();
		for (const [compositeKey, rows] of groupEntries) {
			const separatorIndex = compositeKey.indexOf(" | ");
			const normalizedKey =
				separatorIndex >= 0 && TIME_RANGE_PREFIX_REGEX.test(compositeKey)
					? `${sheetKey} | ${compositeKey.substring(separatorIndex + 3)}`
					: TIME_RANGE_PREFIX_REGEX.test(compositeKey)
						? sheetKey
						: compositeKey;
			const existing = deduped.get(normalizedKey);
			if (existing) {
				existing[1].push(...rows);
			} else {
				deduped.set(normalizedKey, [normalizedKey, [...rows]]);
			}
		}
		out.set(sheetKey, [...deduped.values()]);
	}
	return out;
};
